import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusChip from "@/components/StatusChip";
import RecoveryDrawer from "@/components/RecoveryDrawer";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { Plus, Search, Calendar, Flag, Loader2, Inbox, FileText, ShieldCheck, X, ChevronDown, CloudOff, CloudUpload, RefreshCw, ShieldAlert, Gavel, BadgeCheck, Send, Image as ImageIcon, Camera } from "lucide-react";
import CountUp from "@/components/CountUp";
import { toast } from "sonner";
import { isNetworkError, flushOfflineQueue } from "@/lib/offlineQueue";
import { useAccess } from "@/lib/useAccess";
import { getActiveModule, usesTracker } from "@/lib/module";
import { fmtDate } from "@/lib/datetime";
import {
  cacheInspectionsList, getCachedInspectionsList,
  createLocalInspection, getLocalInspections,
} from "@/lib/offlineCache";

const Dashboard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [q, setQ] = useState("");
  const [statutory, setStatutory] = useState(null); // { orgType, pending }
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const access = useAccess();
  const isAdmin = user?.role === "admin";

  const loadInspections = async () => {
    try {
      const data = (await api.get("/inspections", { params: { module: getActiveModule() } })).data;
      const locals = await getLocalInspections();
      setItems([...locals, ...data]);
      setOffline(false);
      cacheInspectionsList(data);
    } catch {
      // Offline / dead zone — show the last-synced list + device-only inspections
      const cachedWrap = await getCachedInspectionsList();
      const locals = await getLocalInspections();
      if (cachedWrap || locals.length) {
        setItems([...locals, ...(cachedWrap?.list || [])]);
        setOffline(true);
      } else {
        toast.error("Could not load inspections");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { loadInspections(); }, []);

  // Push device-only inspections (created offline in the field) up to the cloud.
  const syncNow = async () => {
    if (!navigator.onLine) { toast.error("You're offline — connect to the internet, then tap Sync now"); return; }
    setSyncing(true);
    try {
      const { created, failed } = await flushOfflineQueue();
      await loadInspections();
      if (failed.length) {
        toast.error(
          `${failed.length} inspection${failed.length === 1 ? "" : "s"} couldn't sync: ${failed[0].error}`,
          { duration: 8000 },
        );
      } else if (created > 0) {
        toast.success(`${created} inspection${created === 1 ? "" : "s"} synced to the cloud`);
      } else {
        toast.success("Everything is already in sync");
      }
    } catch {
      toast.error("Sync failed — please try again");
    } finally { setSyncing(false); }
  };

  // Statutory queue widget — silent best-effort (hidden offline / on failure)
  useEffect(() => {
    (async () => {
      try {
        const [cfg, pend] = await Promise.all([
          api.get("/statutory/config"),
          api.get("/statutory/pending"),
        ]);
        setStatutory({ orgType: cfg.data.org_type || "private_certifier", pending: pend.data || [] });
      } catch { /* offline or transient — widget simply hidden */ }
    })();
  }, []);

  // Lazy-fetch templates only when picker opens — avoids a needless GET on every page load
  const ensureTemplates = async () => {
    if (templates.length) return;
    try { const r = await api.get("/templates", { params: { module: getActiveModule() } }); setTemplates(r.data.filter((t) => !t.archived)); }
    catch { /* picker still works with built-in */ }
  };

  const filtered = useMemo(() => {
    const list = items.filter((i) => {
      if (filter !== "all" && i.status !== filter) return false;
      if (!q.trim()) return true;
      const items = i.items || {};
      const pc_sr = items.pc_sr?.answer || "";
      const officer = items.authorised_officer?.answer || "";
      const address = items.property_address?.answer || "";
      const haystack = `${i.title} ${address} ${pc_sr} ${officer}`.toLowerCase();
      return haystack.includes(q.toLowerCase());
    });
    // Most-recently-updated first, so an in-progress inspection is at the top.
    return list.slice().sort((a, b) => {
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    });
  }, [items, q, filter]);

  const stats = useMemo(() => ({
    total: items.length,
    compliant: items.filter((i) => i.status === "compliant").length,
    non: items.filter((i) => i.status === "non_compliant").length,
    draft: items.filter((i) => i.status === "draft").length,
  }), [items]);

  const unsyncedCount = useMemo(() => items.filter((i) => i.local).length, [items]);

  const openPicker = async () => {
    await ensureTemplates();
    setPickerOpen(true);
  };

  // Fast path: main "New inspection" button creates directly with the NSW built-in.
  // Users who want a different template click the chevron dropdown next to it.
  const onCreateDefault = () => onCreate(null);

  const onCreate = async (templateId = null) => {
    if (access.read_only) {
      toast.error("Your account is in Read-Only Access mode — reactivate your subscription to create inspections", { id: "read-only-mode" });
      return;
    }
    setCreating(true);
    setPickerOpen(false);
    const now = new Date().toISOString();
    const title = newTitle.trim() || `Inspection — ${new Date().toLocaleString()}`;
    const payload = { title, items: {}, template_id: templateId, module: getActiveModule() };
    // Built-in AS1926 gets the inspection_datetime seed; custom templates skip it
    if (!templateId && getActiveModule() === "pool_spa") {
      payload.items = { inspection_datetime: { answer: now, notes: "", photos: [] } };
    }
    try {
      const r = await api.post("/inspections", payload);
      navigate(`/inspections/${r.data.id}`);
    } catch (e) {
      if (isNetworkError(e)) {
        // Offline — create on this device, sync automatically later.
        // Custom templates need the server, so offline always uses the built-in checklist.
        const local = await createLocalInspection({
          ...payload,
          template_id: null,
          items: Object.keys(payload.items).length ? payload.items
            : { inspection_datetime: { answer: new Date().toISOString(), notes: "", photos: [] } },
        });
        toast.info("You're offline — inspection created on this device and will sync automatically");
        navigate(`/inspections/${local.id}`);
      } else {
        toast.error(e?.response?.data?.detail || "Could not create inspection");
      }
    }
    finally { setCreating(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <RecoveryDrawer />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Inspections</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">All inspections</h1>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="new-title-input"
            placeholder="Optional title for the new inspection"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="h-14 px-3 w-64 border-2 border-slate-300 focus:border-[#1E1B4B] focus:outline-none text-base"
          />
          <div className="flex">
            <button onClick={onCreateDefault} disabled={creating} data-testid="new-inspection-button"
              title="Create using NSW AS1926.1-2012"
              className="h-14 px-6 inline-flex items-center justify-center gap-2 font-bold bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 border-r-0">
              {creating ? <Loader2 className="animate-spin" size={18}/> : <Plus size={20} strokeWidth={2.5}/>}
              New inspection
            </button>
            <button onClick={openPicker} disabled={creating} data-testid="new-inspection-choose-template"
              title="Choose a different checklist"
              aria-label="Choose a different checklist"
              className="h-14 w-11 inline-flex items-center justify-center bg-[#1E1B4B] text-white border-2 border-[#1E1B4B] border-l border-l-white/20 hover:bg-[#0F0E2E] disabled:opacity-60">
              <ChevronDown size={18} strokeWidth={2.5}/>
            </button>
          </div>
        </div>
      </div>

      {pickerOpen && (
        <div data-testid="template-picker-overlay" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
          <div className="bg-white border-2 border-slate-900 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b-2 border-slate-200 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Choose a checklist</p>
                <h2 className="text-lg font-black text-slate-900">Which checklist would you like to use?</h2>
                <p className="text-xs text-slate-500 mt-0.5">NSW AS1926.1-2012 is the default. Pick a custom template only if this inspection needs one.</p>
              </div>
              <button onClick={() => setPickerOpen(false)} data-testid="picker-close" className="h-9 w-9 inline-flex items-center justify-center hover:bg-slate-100"><X size={16}/></button>
            </div>
            <div className="divide-y divide-slate-100">
              <button onClick={() => onCreate(null)} data-testid="pick-template-builtin"
                className="w-full text-left px-5 py-4 hover:bg-slate-50 flex items-start gap-3">
                <ShieldCheck size={22} className="text-emerald-700 mt-0.5 shrink-0" strokeWidth={2.5}/>
                <div className="flex-1">
                  <p className="font-black text-slate-900 flex items-center gap-2">
                    AS1926.1-2012 (Built-in)
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 border border-emerald-300">Default</span>
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">The standard AS1926.1-2012 pool &amp; spa checklist for NSW. Always available.</p>
                </div>
              </button>
              {templates.filter((t) => t.is_published || (isAdmin && !t.archived)).length === 0 && (
                <p data-testid="picker-no-custom" className="px-5 py-3 text-xs text-slate-500 italic">No custom templates published yet — your admin can publish one from /admin/builder.</p>
              )}
              {templates.filter((t) => t.is_published || (isAdmin && !t.archived)).map((t) => (
                <button key={t.id} onClick={() => onCreate(t.id)} data-testid={`pick-template-${t.id}`}
                  className="w-full text-left px-5 py-4 hover:bg-slate-50 flex items-start gap-3">
                  <div className="w-[22px] h-[22px] bg-[#1E1B4B] text-white text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5">{t.is_published ? `v${t.published_version}` : "…"}</div>
                  <div className="flex-1">
                    <p className="font-black text-slate-900 flex items-center gap-2 flex-wrap">
                      {t.name}
                      {t.is_default && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">· Default</span>}
                      {!t.is_published && <span data-testid={`draft-badge-${t.id}`} className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 px-1.5 py-0.5 border border-amber-300">Draft</span>}
                    </p>
                    {t.description && <p className="text-xs text-slate-600 mt-0.5">{t.description}</p>}
                    <p className="text-[11px] text-slate-400 mt-0.5">{t.section_count ?? (t.sections || []).length} sections · {t.question_count ?? (t.sections || []).reduce((n, s) => n + (s.questions?.length || 0), 0)} questions</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {offline && (
        <div data-testid="dashboard-offline-banner" className="mb-6 px-4 py-3 bg-amber-50 border-2 border-amber-300 text-sm text-amber-900 flex items-center gap-2">
          <CloudOff size={16} className="shrink-0" strokeWidth={2.5}/>
          <span><strong>You&apos;re offline.</strong> Showing the copies saved on this device — everything syncs automatically when you&apos;re back online.</span>
        </div>
      )}

      {unsyncedCount > 0 && (
        <div data-testid="dashboard-unsynced-banner" className="mb-6 px-4 py-3 bg-amber-50 border-2 border-amber-400 text-sm text-amber-900 flex flex-col sm:flex-row sm:items-center gap-3">
          <CloudUpload size={18} className="shrink-0" strokeWidth={2.5}/>
          <span className="flex-1">
            <strong>{unsyncedCount} inspection{unsyncedCount === 1 ? " is" : "s are"} saved only on this device</strong> and {unsyncedCount === 1 ? "hasn't" : "haven't"} reached the cloud yet. They won&apos;t show on your other devices or the web until synced.
          </span>
          <button
            data-testid="dashboard-sync-now-btn"
            onClick={syncNow}
            disabled={syncing}
            className="shrink-0 h-10 px-4 inline-flex items-center justify-center gap-2 bg-amber-900 text-white font-bold text-xs uppercase tracking-wider hover:bg-amber-800 disabled:opacity-60">
            {syncing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14} strokeWidth={2.5}/>}
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>
      )}

      <OnboardingChecklist />

      {statutory && !usesTracker() && <StatutoryQueue orgType={statutory.orgType} pending={statutory.pending} items={items} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <StatCard label="Total Inspections" value={stats.total} testid="stat-total" />
        <StatCard label="Compliant" value={stats.compliant} total={stats.total} tone="emerald" testid="stat-compliant" />
        <StatCard label="Non-Compliant" value={stats.non} total={stats.total} tone="red" testid="stat-non-compliant" />
        <StatCard label="Incomplete" value={stats.draft} total={stats.total} tone="amber" testid="stat-incomplete" />
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-6 rounded-xl border border-[#1E293B] bg-[#0E1526] p-2.5">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="search-inspections-input" placeholder="Search by address, title, officer, PC/SR…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="h-11 w-full pl-11 pr-4 text-base bg-transparent border-none focus:outline-none text-slate-200"/>
        </div>
        <div className="flex gap-2 overflow-x-auto items-center">
          {[{id:"all",label:"All"},{id:"compliant",label:"Compliant"},{id:"non_compliant",label:"Non-Compliant"},{id:"draft",label:"Incomplete"}].map((f) => (
            <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)}
              className={`h-10 px-5 font-bold text-xs uppercase tracking-wide rounded-full border whitespace-nowrap transition-colors ${
                filter === f.id
                  ? "bg-[#1B2440] text-white border-slate-400"
                  : "bg-transparent text-slate-400 border-[#2A3655] hover:border-slate-400 hover:text-slate-200"
              }`}>{f.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center text-slate-500"><Loader2 className="animate-spin"/></div>
      ) : filtered.length === 0 ? (
        <EmptyState onCreate={onCreate} hasAny={items.length > 0} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-in" data-testid="inspections-grid">
          {filtered.map((i) => {
            const it = i.items || {};
            const address = (it.property_address?.answer || "").trim();
            const primary = address || (i.title || "").trim();
            const dateLine = it.inspection_datetime?.answer
              ? fmtDate(it.inspection_datetime.answer)
              : (i.updated_at ? fmtDate(i.updated_at) : "No date");
            const photoCount = i.photos_total ?? null;
            return (
              <Link key={i.id} to={`/inspections/${i.id}`} data-testid={`inspection-card-${i.id}`}>
                <div className="card-lift rounded-xl bg-[#0E1526] border border-[#1E293B] hover:border-[#00F2C3]/50 p-4 h-full flex flex-col gap-3 transition-colors">
                  <div className="flex gap-3">
                    {i.thumb ? (
                      <img src={i.thumb} alt="" loading="lazy" decoding="async"
                        className="w-20 h-20 rounded-lg object-cover shrink-0 border border-[#1E293B]" />
                    ) : (
                      <div className="w-20 h-20 rounded-lg shrink-0 border border-[#1E293B] bg-[#131A2E] flex items-center justify-center">
                        <Camera size={22} className="text-slate-600" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-white text-[15px] leading-snug line-clamp-2" data-testid={`card-address-${i.id}`}>
                        {primary || <span className="text-slate-500 font-bold italic">Address not set</span>}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1.5" data-testid={`card-date-${i.id}`}>
                        <Calendar size={12} strokeWidth={2.5}/> {dateLine}
                      </div>
                      {it.pool_type?.answer && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                          <FileText size={12} strokeWidth={2.5}/> {it.pool_type.answer}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-auto flex items-center gap-2 flex-wrap">
                    <StatusChip status={i.status} testid={`card-status-${i.id}`} />
                    {i.local && (
                      <span data-testid={`card-local-badge-${i.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[9px] font-bold uppercase tracking-wider">
                        <CloudUpload size={10} strokeWidth={2.5}/> Not synced
                      </span>
                    )}
                    {i.flagged_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-red-400">
                        <Flag size={13} strokeWidth={3} /> {i.flagged_count}
                      </span>
                    )}
                  </div>
                  <div className="pt-3 border-t border-[#1E293B] flex items-center justify-between text-xs">
                    <span className="text-slate-500 inline-flex items-center gap-1.5">
                      <ImageIcon size={12} /> {photoCount !== null ? `${photoCount} photo${photoCount === 1 ? "" : "s"}` : "—"}
                    </span>
                    <span className="text-slate-400">Score <span className="font-bold text-slate-200">{i.score_obtained ?? 0}/{i.score_total ?? 0}</span></span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RingBadge = ({ pct, color, testid }) => {
  const [angle, setAngle] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setAngle(pct); return; }
    let raf;
    const start = performance.now();
    const dur = 1000;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setAngle(pct * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pct]);
  return (
    <span data-testid={testid}
      className="absolute top-2.5 right-2.5 w-[34px] h-[34px] rounded-full flex items-center justify-center"
      style={{ background: `conic-gradient(${color} ${angle * 3.6}deg, rgba(0,0,0,0.12) 0deg)` }}>
      <span className="w-[26px] h-[26px] rounded-full bg-[#0E1526] flex items-center justify-center text-[9px] font-black tabular-nums text-slate-200">
        {Math.round(angle)}%
      </span>
    </span>
  );
};

const StatCard = ({ label, value, total, tone = "teal", testid }) => {
  const t = {
    teal:    { border: "border-[#00F2C3]/50", text: "text-[#00F2C3]", ring: "#00F2C3", glow: "0 0 22px rgba(0,242,195,0.14), inset 0 0 20px rgba(0,242,195,0.04)" },
    emerald: { border: "border-emerald-400/50", text: "text-emerald-300", ring: "#34D399", glow: "0 0 22px rgba(52,211,153,0.13), inset 0 0 20px rgba(52,211,153,0.04)" },
    red:     { border: "border-red-400/50", text: "text-red-300", ring: "#F87171", glow: "0 0 22px rgba(248,113,113,0.13), inset 0 0 20px rgba(248,113,113,0.04)" },
    amber:   { border: "border-yellow-400/50", text: "text-yellow-300", ring: "#FACC15", glow: "0 0 22px rgba(250,204,21,0.13), inset 0 0 20px rgba(250,204,21,0.04)" },
  }[tone];
  const pct = total > 0 ? Math.round((value / total) * 100) : null;
  return (
    <div data-testid={testid} className={`card-lift relative rounded-xl border ${t.border} bg-[#0E1526] p-4 sm:p-5 text-center overflow-hidden`}
      style={{ boxShadow: t.glow }}>
      {pct !== null && <RingBadge pct={pct} color={t.ring} testid={`${testid}-pct`} />}
      <div className={`text-xs sm:text-sm font-bold ${t.text}`}>{label}</div>
      <div className="text-3xl sm:text-4xl font-black tracking-tight mt-1.5 text-white"><CountUp value={value} /></div>
    </div>
  );
};

const EmptyState = ({ onCreate, hasAny }) => (
  <div className="border-2 border-dashed border-slate-300 p-12 text-center" data-testid="empty-state">
    <div className="w-14 h-14 mx-auto bg-slate-100 flex items-center justify-center mb-4">
      <Inbox size={26} className="text-slate-500" strokeWidth={2.5}/>
    </div>
    <h3 className="text-xl font-black text-slate-900">{hasAny ? "No matching inspections" : "No inspections yet"}</h3>
    <p className="text-slate-600 mt-1">{hasAny ? "Try a different search or filter." : "Create your first inspection to get started."}</p>
    {!hasAny && (
      <button onClick={onCreate} data-testid="empty-state-create-button" className="mt-6 h-12 px-6 inline-flex items-center gap-2 bg-[#1E1B4B] text-white font-bold border-2 border-[#1E1B4B] hover:bg-[#0F0E2E]">
        <Plus size={18} strokeWidth={2.5}/> New inspection
      </button>
    )}
  </div>
);

const QueueCard = ({ icon: Icon, label, value, sub, urgent, testid }) => (
  <Link to="/statutory" data-testid={testid}
    className={`flex items-center gap-3 border-2 p-3 bg-white hover:border-slate-900 transition-colors ${urgent ? "border-red-400" : "border-slate-200"}`}>
    <div className={`w-9 h-9 shrink-0 flex items-center justify-center ${urgent ? "bg-red-600" : "bg-[#1E1B4B]"} text-white`}>
      <Icon size={16} strokeWidth={2.5}/>
    </div>
    <div className="min-w-0">
      <p className="text-xl font-black text-slate-900 leading-none">{value}</p>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mt-1">{label}</p>
      {sub && <p className={`text-[11px] font-bold ${urgent ? "text-red-600" : "text-slate-400"}`}>{sub}</p>}
    </div>
  </Link>
);

const StatutoryQueue = ({ orgType, pending, items }) => {
  const isCouncil = orgType === "council";
  const overdueCount = pending.filter((r) => (r.deadlines || []).some((d) => d.days_remaining < 0)).length;

  if (isCouncil) {
    const nonCompliant = (i) => i.compliance_status === "non_compliant" || i.status === "non_compliant";
    const enforcement = items.filter((i) => nonCompliant(i) && i.nc_certificate_issued_at && !i.compliance_certificate_issued_at).length;
    const month = new Date().toISOString().slice(0, 7);
    const rectified = items.filter((i) => (i.compliance_certificate_issued_at || "").startsWith(month)).length;
    if (!pending.length && !enforcement && !rectified) return null;
    return (
      <div className="mb-8" data-testid="council-queue">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1.5">
          <Gavel size={11} strokeWidth={3}/> Council actions
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <QueueCard icon={ShieldAlert} label="Certificates due" value={pending.length}
            sub={overdueCount ? `${overdueCount} overdue` : "7-day clock"} urgent={overdueCount > 0} testid="queue-certs-due"/>
          <QueueCard icon={Gavel} label="Under enforcement" value={enforcement} sub="Cert issued, not yet rectified" testid="queue-enforcement"/>
          <QueueCard icon={BadgeCheck} label="Rectified this month" value={rectified} sub="Compliance certs issued" testid="queue-rectified"/>
        </div>
      </div>
    );
  }

  const certsDue = pending.filter((r) => (r.deadlines || []).some((d) => d.kind === "nc_certificate")).length;
  const councilDue = pending.filter((r) => (r.deadlines || []).some((d) => d.kind === "council_submission")).length;
  if (!certsDue && !councilDue) return null;
  return (
    <div className="mb-8" data-testid="certifier-queue">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1.5">
        <ShieldAlert size={11} strokeWidth={3}/> Statutory queue
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <QueueCard icon={FileText} label="NC certificates due" value={certsDue} sub="7-day clock" testid="queue-certs-due"/>
        <QueueCard icon={Send} label="Council submissions due" value={councilDue}
          sub={overdueCount ? `${overdueCount} overdue` : "42-day clock"} urgent={overdueCount > 0} testid="queue-council-due"/>
      </div>
    </div>
  );
};

export default Dashboard;
